import { Card, Col, Row } from 'antd';
import dayjs from 'dayjs';
import { endsWith, isFunction } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IPrehledy } from 'types/spravceUctuType';

import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import stahnoutPdfApi from 'services/akcionar/stahnoutPdf';
import fileDownload from 'services/fileDownload';
import zrusKorporatniAkciApi from 'services/korporatniAkce/zrusKorporatniAkci';
import spravceStahnoutPdf from 'services/spravceUctu/spravceStahnoutPdf';

import { EStavHl } from 'enums/akcionarKorpAkce/stavHl';
import { ETypUdalosti } from 'enums/akcionarKorpAkce/typUdalosti';
import { ENotificationType } from 'enums/notificationType';
import { splitUrl } from 'enums/regExp';
import routePathEnum from 'enums/routePathEnum';

interface IAkcionarCard {
    prehled: IPrehledy[];
    showButton?: boolean;
    pocetDlazdic?: number;
    // Pokud potrebujeme znovu nacist dlazdice, tak si posilame callbackem zmenu
    reload?: (val: boolean) => void;
    isSpravce?: boolean;
    noButtons?: boolean;
}
const retTranslate = (isin: string, nazevemise: string, typudalosti: string, rozpracovano: boolean) => (
    <p>
        {isin} - {nazevemise} <br />({<TranslateText>{typudalosti}</TranslateText>})
        {rozpracovano && <TranslateText>KORPORATNI_AKCE_ROZPRACOVANO</TranslateText>}
    </p>
);
const AkcionarCard: React.FC<IAkcionarCard> = props => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const location = useLocation();
    const { setLevel } = useProfily();
    const { aktivniFirma, NotificationComponent } = useContext(Context);
    const { prehled, showButton = false, pocetDlazdic = 4, reload } = props;
    const [dlazdice, setDlazdice] = useState<number>(pocetDlazdic - 1);
    const [loading, setLoading] = useState<{ loading: boolean }[]>([]);
    const splitURL = location.pathname.split(splitUrl)[0].split(splitUrl)[0];
    const novaUdalostUrl = `${splitURL}/${aktivniFirma?.iducet}${routePathEnum.NOVA_UDALOST}`;
    const pathDetail = `${location.pathname}${routePathEnum.DETAIL_KORPORATNI_AKCE}`;
    const pruvodceVH = `${novaUdalostUrl}${routePathEnum.PRUVODCE_SRD_VH}`;
    const pruvodceVYPIS = `${novaUdalostUrl}${routePathEnum.PRUVODCE_SRD_VYPIS}`;

    useEffect(() => {
        setLoading(
            prehled.map(() => {
                return {
                    loading: false
                };
            })
        );
    }, [prehled]);

    const zrusitKorporatniAkci = (id: number, index: number) => {
        setLoading(prevState => {
            const newState = [...prevState];
            newState[index].loading = true;
            return newState;
        });
        zrusKorporatniAkciApi(id)
            .then(() => {
                if (isFunction(reload)) reload(true);
                NotificationComponent({
                    type: ENotificationType.SUCCESS,
                    title: 'ZRUSIT_ZADOST',
                    description: 'ZRUSIT_ZADOST_SUCCESS'
                });
            })
            .finally(() => {
                if (isFunction(reload)) reload(true);
                setLoading(prevState => {
                    const newState = [...prevState];
                    newState[index].loading = false;
                    return newState;
                });
            });
    };
    return (
        <>
            <Row gutter={16} justify="start">
                {prehled.map((akce, index) => {
                    if (index > dlazdice) return;
                    return (
                        <Col
                            span={12}
                            key={index}
                            style={{
                                marginBottom: 16
                            }}
                        >
                            <Card
                                title={retTranslate(akce.isin, akce.nazevemise, akce.typudalosti, !!akce.rozpracovano)}
                            >
                                <Paragraph text="SRD_DLAZDICE_DATUM_KONANI" />:
                                {akce.datumkonani ? dayjs(akce.datumkonani).format(' DD.MM.YYYY HH:mm') : ' -'}
                                <br />
                                {akce.elhlasovani &&
                                    akce.rozhodnyden &&
                                    dayjs().diff(dayjs(akce.rozhodnyden), 'minutes') < 0 && (
                                        <>
                                            <Paragraph text={'SRD_PRUVODCE_VH_E_HLASOVAT_OD'} />
                                            {dayjs(akce.rozhodnyden).format(' DD.MM.YYYY')}
                                            <br />
                                        </>
                                    )}
                                {akce.eltermin && (
                                    <>
                                        <Paragraph text="SRD_PRUVODCE_VH_E_TERMIN" />:{' '}
                                        {dayjs(akce.eltermin).format(' DD.MM.YYYY HH:mm')}
                                    </>
                                )}
                                {akce.vypis && (
                                    <>
                                        <Paragraph text="SRD_DLAZDICE_VYPIS_AKCIONARU" />:{' '}
                                        <Paragraph text={akce.vypis} />
                                    </>
                                )}
                                {akce.predano && (
                                    <>
                                        <Paragraph text="AKCIONAR_PREDANO" />:{' '}
                                        {dayjs(akce.predano).format(' DD.MM.YYYY HH:mm')}
                                    </>
                                )}
                                {akce.potvrzeno && (
                                    <>
                                        <Paragraph text="AKCIONAR_POTVRDZENO" />:{' '}
                                        {dayjs(akce.potvrzeno).format(' DD.MM.YYYY HH:mm')}
                                    </>
                                )}
                                {!endsWith(location.pathname, routePathEnum.KORPORATNI_AKCE) &&
                                loading.length === prehled.length ? (
                                    <ButtonComponent
                                        id={`akcionarCardPdfBtn${index}`}
                                        buttontext="STAHNOUT_PDF"
                                        className="akcionar-card-button"
                                        loading={loading[index].loading}
                                        onClick={() => {
                                            setLoading(prevState => {
                                                const newState = [...prevState];
                                                newState[index].loading = true;
                                                return newState;
                                            });
                                            !endsWith(location.pathname, routePathEnum.PREHLED_SPRAVCE) &&
                                            !endsWith(location.pathname, routePathEnum.PREHLED_UCASTNIK_HLASOVANI)
                                                ? stahnoutPdfApi(String(akce.id)).then(({ data }) => {
                                                      fileDownload(data.redirect).then(() =>
                                                          setLoading(prevState => {
                                                              const newState = [...prevState];
                                                              newState[index].loading = false;
                                                              return newState;
                                                          })
                                                      );
                                                  })
                                                : spravceStahnoutPdf(String(akce.id)).then(({ data }) => {
                                                      fileDownload(data.redirect).then(() =>
                                                          setLoading(prevState => {
                                                              const newState = [...prevState];
                                                              newState[index].loading = false;
                                                              return newState;
                                                          })
                                                      );
                                                  });
                                        }}
                                    />
                                ) : null}
                                {!props.noButtons && (
                                    <Row>
                                        <Col
                                            style={{
                                                marginTop: 15
                                            }}
                                        >
                                            {endsWith(location.pathname, routePathEnum.KORPORATNI_AKCE) && (
                                                <ButtonComponent
                                                    id={`akcionarCardDetailBtn${index}`}
                                                    buttontext={akce.rozpracovano ? 'POKRACOVAT' : 'ZOBRAZIT_DETAIL'}
                                                    className="akcionar-card-button"
                                                    onClick={() => {
                                                        setLevel('idSRD', akce.id);
                                                        setLevel('isinSRD', akce.isin);
                                                        akce.rozpracovano && setLevel('prevPath', location.pathname);
                                                        akce.rozpracovano
                                                            ? navigateWorkaround(
                                                                  akce.typudalosti === ETypUdalosti.VH
                                                                      ? pruvodceVH
                                                                      : pruvodceVYPIS
                                                              )
                                                            : navigateWorkaround(`${pathDetail}/${akce.id}`);
                                                    }}
                                                />
                                            )}
                                            {!endsWith(location.pathname, routePathEnum.KORPORATNI_AKCE)
                                                ? akce.elhlasovani &&
                                                  (endsWith(location.pathname, routePathEnum.PREHLED_SPRAVCE) &&
                                                  props.isSpravce ? (
                                                      <>
                                                          <ButtonComponent
                                                              id={`akcionarCardDetailAltBtn${index}`}
                                                              buttontext="DETAIL"
                                                              onClick={() => setLevel('spravceSelectedCard', akce)}
                                                              className="akcionar-card-button"
                                                          />
                                                      </>
                                                  ) : (
                                                      ((dayjs().diff(dayjs(akce.eltermin), 'hours') < 0 &&
                                                          dayjs().diff(dayjs(akce.rozhodnyden), 'hours') > 0) ||
                                                          akce.stavhl === EStavHl.HLASOVANO) && (
                                                          <>
                                                              <ButtonComponent
                                                                  id={`akcionarCardHlasovaniBtn${index}`}
                                                                  className="akcionar-card-button"
                                                                  buttontext={
                                                                      akce.stavhl === EStavHl.HLASOVANO
                                                                          ? 'SRD_PRUVODCE_VH_E_PRIDAT_VOTETP_HLASOVANO'
                                                                          : 'SRD_PRUVODCE_VH_E_PRIDAT_VOTETP'
                                                                  }
                                                                  onClick={() =>
                                                                      navigateWorkaround(
                                                                          // eslint-disable-next-line max-len
                                                                          `${splitURL}/${akce.id}${routePathEnum.HLASOVANI_AKCIONAR}`
                                                                      )
                                                                  }
                                                              />
                                                          </>
                                                      )
                                                  ))
                                                : akce.rozpracovano && (
                                                      <ButtonComponent
                                                          id={`akcionarZrusitBtn${index}`}
                                                          buttontext="ZRUSIT"
                                                          className="akcionar-card-button"
                                                          onClick={() => zrusitKorporatniAkci(akce.id, index)}
                                                          loading={loading[index]?.loading}
                                                      />
                                                  )}
                                        </Col>
                                    </Row>
                                )}
                            </Card>
                        </Col>
                    );
                })}
            </Row>
            {showButton && (
                <Row justify="center">
                    <ButtonComponent
                        id="nacistDalsiBtn"
                        buttontext="NACIST_DALSI_ZAZNAMY"
                        onClick={() => setDlazdice(val => val + 4)}
                        className="akcionar-card-button"
                    />
                </Row>
            )}
        </>
    );
};

export default AkcionarCard;
